import { signedPct, signedUnits } from "~/components/demand-2/shared/demandBreakdownShared"
import { AnswerPicker } from "~/components/demand-2/shared/FramingAnswerPicker"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { useSeasonSalesTrend } from "~/components/demand-2/useDemandIntents"
import { api } from "~/utils/api"
import { formatNumber } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import type { InsightScalars } from "~/utils/demandInsightValues"
import {
  effectiveAnswerAt,
  type FramingDescribeContext,
  type FramingNode,
  WHOLE_COLLECTION_NODE,
} from "~/utils/demandFraming"
import { modeUnits, type SeasonTrendMode } from "~/utils/demandSeasonTrendMode"

/**
 * The units growth can land on: each variant's baseline less its pre-orders, as the engine splits
 * it (`seasonTrendBase`). Null until the offers read carries it.
 */
export const unconfirmedGrowthBase = (
  variants: readonly IVariantSummary[],
  scalarsFor: (variantId: string) => InsightScalars | null,
): number | null => {
  let sum = 0
  let seen = false
  for (const v of variants) {
    const base = scalarsFor(v.variantId)?.seasonTrendBase
    if (base == null) continue
    sum += base
    seen = true
  }
  return seen ? sum : null
}

/** The `seasonTrend` question: how "Growth from comparable seasons" applies its measured rate. */
const SeasonTrendAnswer = ({
  node = WHOLE_COLLECTION_NODE,
  measured,
  unconfirmed = null,
}: {
  node?: FramingNode
  /** A node's own figures (`sumNodeTrend`); the route's lineage otherwise. */
  measured?: { pct: number | null; year1: number; year2: number; appliedPct?: number | null }
  /** `unconfirmedGrowthBase` over the same variants. */
  unconfirmed?: number | null
}) => {
  const { collectionIds } = useDemand2Scope()
  const trend = useSeasonSalesTrend()
  // A group resolves through the same workspace as any member.
  const collectionId = collectionIds[0]
  const answersQ = api.demand2.getDemandFraming.useQuery(
    { collectionId: collectionId ?? 0 },
    { enabled: collectionId != null },
  )
  if (collectionId == null || answersQ.isLoading) return null
  const { answer, inherited, isDefault } = effectiveAnswerAt(
    answersQ.data?.answers ?? [],
    "seasonTrend",
    node,
  )
  const figures = measured ?? trend
  const context: FramingDescribeContext = {
    season:
      trend.comparable && trend.prior
        ? {
            pct: figures.pct == null ? null : Math.round(figures.pct * 10) / 10,
            appliedPct: measured?.appliedPct ?? null,
            nodeName: node.groupKey === "" ? undefined : node.groupValue,
            year1: figures.year1,
            unitsDelta: figures.year1 - figures.year2,
            comparableName: trend.comparable.names.join(", "),
            priorName: trend.prior.names.join(", "),
            byCategory: trend.levelKeys.length > 0,
          }
        : undefined,
  }
  // The same `modeUnits` the engine splits from, so the preview matches the result.
  const comparableName = trend.comparable?.names.join(", ") ?? "the comparable collection"
  const impactOf = (optionId: string, pct: number | null): React.ReactNode => {
    if (figures.pct == null || figures.year1 <= 0) return null
    const mode = modeOf(optionId, pct)
    if (mode == null) return null
    const units = modeUnits(mode, figures)
    if (units == null) return null
    return (
      <span className="flex flex-col gap-0.5">
        <span className="paragraph-sm text-fg-subtle">
          <span className={units >= 0 ? "text-fg-success-icon" : "text-fg-error-icon"}>
            {signedUnits(units)} units · {signedPct(units / figures.year1)}
          </span>{" "}
          vs. {comparableName}
        </span>
        {unconfirmed != null && Math.round(units) !== 0 && (
          <UnconfirmedNote units={units} unconfirmed={unconfirmed} />
        )}
      </span>
    )
  }
  return (
    <AnswerPicker
      collectionId={collectionId}
      question="seasonTrend"
      answer={answer}
      node={node}
      inherited={inherited}
      isDefault={isDefault}
      describeContext={context}
      optionFooter={impactOf}
    />
  )
}

/**
 * An answer past every unit not yet confirmed as pre-orders: the engine caps the cut there
 * (`insightOffers.ts`).
 */
const UnconfirmedNote = ({ units, unconfirmed }: { units: number; unconfirmed: number }) => {
  const preorders = formatName("preorder", true)
  const base = formatNumber(Math.round(unconfirmed))
  if (units < 0 && -units > unconfirmed + 0.5)
    return (
      <span className="paragraph-sm text-fg-error-icon">
        {`Capped at −${base}: only ${base} units are not yet confirmed as ${preorders}, so the plan stops at the confirmed ${preorders}.`}
      </span>
    )
  // Where an answer that fits lands is the table under the chart (`GrowthLandingTable`).
  return null
}

/** Null for a custom option with no value yet. */
const modeOf = (optionId: string, pct: number | null): SeasonTrendMode | null => {
  if (optionId === "pct" || optionId === "pctRaw" || optionId === "units" || optionId === "none")
    return { optionId, customPct: null }
  if (optionId === "custom" && pct != null) return { optionId, customPct: pct }
  if (optionId === "customUnits" && pct != null)
    return { optionId, customPct: null, customUnits: pct }
  return null
}

export default SeasonTrendAnswer
